import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCiAdminBasePath } from '../../contexts/CiAdminAuthContext';
import CiPageChrome from '../../components/CustomerIdentity/CiPageChrome';
import ShipToDirectoryFilters from '../../components/CustomerIdentity/ShipToDirectoryFilters';
import ShipToDirectoryTable from '../../components/CustomerIdentity/ShipToDirectoryTable';
import { CIInfoBar } from '../../components/CustomerIdentity/CustomerIdentityShared';
import { listCustomerAddressesPaginated } from '../../services/customerIdentityApi';

const PAGE_SIZE = 25;

// Cross-customer, cross-site ship-to address directory: filter by customer
// (via type-ahead search, not a dropdown preloaded with every customer --
// see CustomerSearchPicker) and search across both address fields and the
// owning customer/site name (handled server-side by
// list_addresses_directory_paginated). Unlike the "Users" directory,
// picking a customer filters this table in place rather than redirecting
// to that customer's detail page -- this page is meant to be browsed
// across many customers at once.
export default function ShipToAddressesDirectory() {
  const basePath = useCiAdminBasePath();
  const [searchParams] = useSearchParams();
  const siteFilter = searchParams.get('site_id') || '';
  const querySuffix = searchParams.toString() ? `?${searchParams.toString()}` : '';

  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterCustomerLabel, setFilterCustomerLabel] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageMessage, setPageMessage] = useState(null);

  const prevSiteRef = useRef(null);
  useEffect(() => {
    if (prevSiteRef.current === null) {
      prevSiteRef.current = siteFilter;
      return;
    }
    if (prevSiteRef.current !== siteFilter) {
      setFilterCustomerId('');
      setFilterCustomerLabel('');
      prevSiteRef.current = siteFilter;
    }
  }, [siteFilter]);

  useEffect(() => {
    setPage(0);
  }, [siteFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sort_by: sortKey,
        sort_dir: sortDir,
        search: searchApplied.trim() || undefined,
      };
      if (siteFilter) params.site_id = siteFilter;
      if (filterCustomerId) params.customer_id = filterCustomerId;
      const data = await listCustomerAddressesPaginated(params);
      setRows(data?.items || []);
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      setPageMessage({ kind: 'danger', text: err.message || 'Could not load ship-to addresses.' });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [siteFilter, page, sortKey, sortDir, searchApplied, filterCustomerId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));

  const onSort = (key) => {
    setPage(0);
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const applySearch = () => {
    setPage(0);
    setSearchApplied(searchDraft.trim());
  };

  const handleCustomerSelect = (id, label) => {
    setPage(0);
    setFilterCustomerId(id);
    setFilterCustomerLabel(label);
  };

  const handleCustomerClear = () => {
    setPage(0);
    setFilterCustomerId('');
    setFilterCustomerLabel('');
  };

  const showSiteColumn = !siteFilter;

  return (
    <div className="d-flex flex-column h-100">
      <CIInfoBar message={pageMessage} onDismiss={() => setPageMessage(null)} />
      <CiPageChrome
        title="Ship-To Addresses"
        subtitle="Browse ship-to addresses across customers, filter by customer, and search by address or customer/site fields."
      >
        <div className="container-fluid px-0 px-md-1">
          <ShipToDirectoryFilters
            siteId={siteFilter}
            filterCustomerId={filterCustomerId}
            filterCustomerLabel={filterCustomerLabel}
            onCustomerSelect={handleCustomerSelect}
            onCustomerClear={handleCustomerClear}
            searchDraft={searchDraft}
            onSearchDraftChange={setSearchDraft}
            onApplySearch={applySearch}
            loading={loading}
          />
          <ShipToDirectoryTable
            basePath={basePath}
            querySuffix={querySuffix}
            rows={rows}
            loading={loading}
            total={total}
            page={page}
            totalPages={totalPages}
            showSiteColumn={showSiteColumn}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            onPrevPage={() => setPage((p) => Math.max(0, p - 1))}
            onNextPage={() => setPage((p) => p + 1)}
          />
        </div>
      </CiPageChrome>
    </div>
  );
}
